import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Plus, ServerCog as ServerCogIcon } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { NodeHealthBadge } from '@/components/status-badges';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UsageBar } from '@/components/usage-bar';
import { nodesQuery } from '@/lib/admin-queries';

export const Route = createFileRoute('/_app/admin/nodes/')({ component: NodesPage });

function NodesPage() {
  const query = useQuery(nodesQuery);
  return (
    <>
      <PageHeader
        title="Nodes"
        icon={ServerCogIcon}
        description="Machines running hearthd. Each node hosts game server containers."
        actions={
          <Button asChild>
            <Link to="/admin/nodes/new">
              <Plus /> New node
            </Link>
          </Button>
        }
      />
      <QueryState query={query}>
        {(nodes) => (
          <div className="table-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Node</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-56">Memory</TableHead>
                  <TableHead className="w-56">Disk</TableHead>
                  <TableHead className="text-right">Servers</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {nodes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground py-8 text-center">
                      No nodes yet. Add this Mac as your first node.
                    </TableCell>
                  </TableRow>
                )}
                {nodes.map((n) => (
                  <TableRow key={n.id}>
                    <TableCell>
                      <Link
                        to="/admin/nodes/$nodeId"
                        params={{ nodeId: String(n.id) }}
                        className="font-medium hover:underline"
                      >
                        {n.name}
                      </Link>
                      <div className="text-muted-foreground font-mono text-xs">
                        {n.scheme}://{n.fqdn}:{n.daemonPort} · {n.location.short}
                      </div>
                    </TableCell>
                    <TableCell>
                      <NodeHealthBadge health={n.health} />
                      {n.maintenanceMode && (
                        <div className="text-warning mt-1 text-xs">Maintenance</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <UsageBar label="Allocated" used={n.allocated.memoryMb} total={n.memoryMb} />
                    </TableCell>
                    <TableCell>
                      <UsageBar label="Allocated" used={n.allocated.diskMb} total={n.diskMb} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{n.serverCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </QueryState>
    </>
  );
}
